// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { getAdminCounts, getProjects } from '../services/api';
import { gereSonAbonnement, administreUneLicence, peutVerifierCertification } from '../lib/acces';
import { useLogoLicence } from '../lib/logoLicence';
import UpgradeModal from './UpgradeModal';
import {
  Bot, BarChart3, Bookmark, FileStack, FolderPlus, Star, Users,
  MoreHorizontal, Copy, Trash2, Settings, Sliders,
  HelpCircle, FileText, LogOut, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, FolderOpen, Lock, Building2, LayoutDashboard,
  ScrollText, Sparkles, Flag, CreditCard, BadgeCheck,
} from 'lucide-react';

const SIDEBAR_WIDTH = 272;
const NAV_VISIBLE_COUNT = 4;
const LIST_VISIBLE_COUNT = 4;

const Badge = ({ count }) => {
  if (!count || count === 0) return null;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 99, background: '#ef4444', color: 'white', marginLeft: 4, flexShrink: 0, lineHeight: '16px' }}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

const Sidebar = ({ collapsed, onToggleCollapse }) => {
  const [location, setLocation] = useLocation();
  // ✅ FIX : useSearch() pour lire la query string (wouter useLocation
  // retourne uniquement le pathname, sans le ?id=...).
  const searchString = useSearch();
  const currentProjectId = (() => {
    if (location !== '/projets') return null;
    return new URLSearchParams(searchString || '').get('id');
  })();
  const { user, logout, refreshUser } = useAuth();
  const { t } = useI18n();
  const logoLicence = useLogoLicence(user?.license?.logo_path);
  const [recentAnalyses, setRecentAnalyses]   = useState([]);
  const [userMenuOpen, setUserMenuOpen]         = useState(false);
  const [dotMenuIdx, setDotMenuIdx]             = useState(null);
  const [counts, setCounts]                     = useState({ annotations_open: 0, suggestions_open: 0 });
  const [projects, setProjects]                 = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [navExpanded, setNavExpanded]           = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [recentExpanded, setRecentExpanded]     = useState(false);
  const userMenuRef = useRef(null);
  const dotMenuRef  = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isAdmin = ['admin', 'dev'].includes(user?.role);
  // Verrouillé uniquement si un tier est assigné ET qu'il n'inclut pas le mode projet
  // (sans tier assigné = ancien système, accès inchangé — Phase 3/4 tarification).
  const projectsLocked = !!user?.tier_code && !user?.tier_projet_disponible;

  // Récents = les 10 dernières analyses DU COMPTE, sur tous ses postes, lues
  // sur le serveur (décision de Marc du 30/09/2026, chantier ⑮). Avant, c'était
  // une liste d'identifiants gardée dans `localStorage` — propre au navigateur,
  // et visible du compte suivant sur le même poste.
  useEffect(() => {
    // `auteur=moi` : un compte Evalens liste par défaut les analyses de TOUS.
    fetch(`${API_BASE_URL}/api/analyses?limit=10&auteur=moi`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setRecentAnalyses(d?.analyses || []))
      .catch(() => {});
  }, [location, API_BASE_URL]);

  // Charger projets utilisateur
  const chargerProjets = useCallback(() => {
    getProjects()
      .then(data => setProjects(data?.projects || []))
      .catch(() => {});
  }, []);

  useEffect(() => { chargerProjets(); }, [location, chargerProjets]);

  // ⚠️ La liste n'était rechargée qu'au changement de page. Pendant une
  // extraction, la pastille restait donc figée sur l'état d'avant validation :
  // il fallait naviguer ailleurs et revenir pour la voir bouger. Tant qu'au
  // moins un projet extrait, on relit — c'est la seule vue des projets visible
  // en permanence, quelle que soit la page.
  const projetsEnExtraction = projects.some(p => p.extraction_in_progress);
  useEffect(() => {
    if (!projetsEnExtraction) return;
    const id = setInterval(chargerProjets, 5000);
    return () => clearInterval(id);
  }, [projetsEnExtraction, chargerProjets]);

  // ⚠️ Compteur d'analyses du bas de la barre. Il n'était relu qu'au chargement
  // de la page, au login, et quand le tableau de bord recevait la fin de SA
  // propre analyse : une analyse terminée pendant qu'on consultait une autre
  // page, celle d'un collègue sur un pool partagé, ou toute consommation hors
  // de cet écran le laissaient figé jusqu'au prochain F5 (constaté le
  // 15/09/2026). Il est désormais relu à chaque changement de page, au retour
  // sur l'onglet, et toutes les 60 s — `/api/auth/me` ne coûte que quelques
  // requêtes SQL.
  // `refreshUser` est recréé à chaque rendu du contexte : passé par une
  // référence, il ne relance pas les effets en boucle.
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  const premierRendu = useRef(true);

  useEffect(() => {
    // Au premier rendu, AuthContext vient déjà de relire le compte.
    if (premierRendu.current) { premierRendu.current = false; return; }
    refreshUserRef.current();
  }, [location]);

  useEffect(() => {
    const relire = () => refreshUserRef.current();
    const auRetour = () => { if (document.visibilityState === 'visible') relire(); };
    document.addEventListener('visibilitychange', auRetour);
    const id = setInterval(relire, 60000);
    return () => {
      document.removeEventListener('visibilitychange', auRetour);
      clearInterval(id);
    };
  }, []);

  // Badges admin — polling 60s
  useEffect(() => {
    if (!isAdmin) return;
    const load = () => {
      getAdminCounts().then(setCounts).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [isAdmin]);

  // Fermer menus au clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (dotMenuRef.current && !dotMenuRef.current.contains(e.target)) setDotMenuIdx(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => { await logout(); setLocation('/login'); };

  const handleDeleteAnalysis = async (id) => {
    if (!window.confirm(t('delete') + ' ?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/analyses/${id}`, { method: 'DELETE' });
      setRecentAnalyses(prev => prev.filter(a => (a.id || a.analysis_id) !== id));
    } catch(e) {}
    setDotMenuIdx(null);
  };

  const handleCopyAnalysis = (a) => {
    let meta = {};
    try { meta = a.metadata ? JSON.parse(a.metadata) : {}; } catch(_) {}
    let text = `Analyse Normascan — ${a.filename}\n`;
    if (meta.conformity_score != null) text += `Score : ${Math.round(meta.conformity_score)}/100\n`;
    navigator.clipboard.writeText(text).then(() => alert(t('sidebar_copy_done')));
    setDotMenuIdx(null);
  };

  const isActive = (path) => location === path || location.startsWith(path + '?');

  const navItem = (path, icon, label, badge) => (
    <Link key={path} href={path}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: collapsed ? '10px 0' : '9px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
        background: isActive(path) ? 'rgba(59,130,246,0.1)' : 'transparent',
        color: isActive(path) ? 'var(--blue)' : 'var(--black)',
        fontWeight: isActive(path) ? 600 : 400,
        fontSize: 14, opacity: isActive(path) ? 1 : 0.65,
      }}
      onMouseEnter={e => { if (!isActive(path)) e.currentTarget.style.background = 'rgba(128,128,128,0.08)'; }}
      onMouseLeave={e => { if (!isActive(path)) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>
        {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
        {!collapsed && badge > 0 && <Badge count={badge} />}
      </div>
    </Link>
  );

  // "Voir plus" / "Voir moins" — flèche + libellé, sous le dernier item affiché
  const showMoreRow = (expanded, onToggle) => (
    <div
      className="sidebar-item-hover"
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: collapsed ? '8px 0' : '7px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 8, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12,
      }}
    >
      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      {!collapsed && <span>{expanded ? t('sidebar_show_less') : t('sidebar_show_more')}</span>}
    </div>
  );

  const displayName  = user?.display_name || '';
  const emailPrefix  = user?.email?.split('@')[0] || '';
  const displayLabel = displayName || emailPrefix;
  const roleLabelMap = { admin: 'Admin', dev: 'Dev', partner: 'Partenaire', user: 'Utilisateur' };

  // Items du menu principal, filtrés par rôle/droits — construits une fois pour
  // pouvoir en afficher seulement les 4 premiers (+ bouton "Plus")
  const mainNavItems = [
    { path: '/dashboard',        icon: <Bot size={16} />,       label: t('nav_norma') },
    { path: '/analyses',         icon: <FileStack size={16} />, label: t('nav_analyses') },
    { path: '/mes-statistiques', icon: <BarChart3 size={16} />, label: t('nav_my_stats') },
    // Chantier ⑩ : à partir de Pro, même règle que la garde de la route.
    ...(peutVerifierCertification(user) ? [
      { path: '/verifier-certification', icon: <BadgeCheck size={16} />, label: t('nav_verifier_certification') },
    ] : []),
    ...(isAdmin ? [
      { path: '/admin/stats',       icon: <BarChart3 size={16} />, label: t('nav_stats') },
      { path: '/admin/annotations', icon: <Bookmark size={16} />,  label: t('nav_annotations'), badge: counts.annotations_open },
      { path: '/admin/ratings',     icon: <Star size={16} />,      label: t('nav_ratings'), badge: counts.suggestions_open },
      // Le badge compte les signalements NON TRIÉS : c'est la file de travail,
      // pas le volume total. Un compteur qui ne descend jamais cesse d'être lu.
      { path: '/admin/retours',     icon: <Flag size={16} />,      label: t('nav_retours'), badge: counts.retours_a_trier },
      { path: '/admin/users',       icon: <Users size={16} />,     label: t('nav_users') },
      { path: '/admin/enterprise',  icon: <Building2 size={16} />, label: t('nav_enterprise') },
      { path: '/admin/audit',       icon: <ScrollText size={16} />, label: t('nav_audit') },
    ] : []),
    // Même règle que la GARDE de la route (`lib/acces.js`) : l'entrée de menu
    // et l'accès à l'écran ne doivent pas pouvoir diverger.
    ...(administreUneLicence(user) ? [
      { path: '/license-portal', icon: <LayoutDashboard size={16} />, label: t('nav_license_portal') },
    ] : []),
  ];
  const navHasMore = !collapsed && mainNavItems.length > NAV_VISIBLE_COUNT;
  const visibleNavItems = navHasMore && !navExpanded ? mainNavItems.slice(0, NAV_VISIBLE_COUNT) : mainNavItems;

  const projectsHasMore = !collapsed && projects.length > LIST_VISIBLE_COUNT;
  const visibleProjects = projectsHasMore && !projectsExpanded ? projects.slice(0, LIST_VISIBLE_COUNT) : projects;

  const recentHasMore = !collapsed && recentAnalyses.length > LIST_VISIBLE_COUNT;
  const visibleRecent = recentHasMore && !recentExpanded ? recentAnalyses.slice(0, LIST_VISIBLE_COUNT) : recentAnalyses;

  return (
    <>
      <style>{`
        .sidebar-item-hover:hover { background: rgba(128,128,128,0.08) !important; }
        .sidebar-dot-btn:hover { background: rgba(128,128,128,0.12) !important; }
      `}</style>
      <div style={{
        width: collapsed ? 60 : SIDEBAR_WIDTH, minWidth: collapsed ? 60 : SIDEBAR_WIDTH,
        height: '100vh', background: 'var(--bg-surface)',
        borderRight: '1px solid var(--gray)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden', zIndex: 20,
      }}>

        {/* Logo + toggle */}
        <div style={{ padding: collapsed ? '14px 0' : '14px 16px', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', borderBottom: '1px solid var(--gray)', flexShrink: 0 }}>
          {!collapsed && (
            <Link href="/dashboard">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <img src="/N.svg" alt="logo" style={{ width: 28, height: 28 }} />
                <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: 2, color: 'var(--black)' }}>NORMASCAN</span>
              </div>
            </Link>
          )}
          <button
            onClick={onToggleCollapse}
            style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--gray)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--black)', flexShrink: 0 }}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Navigation principale — 4 items visibles, le reste derrière "Plus" (fixe, ne scroll pas) */}
        <div style={{ padding: collapsed ? '8px 6px' : '8px 8px', flexShrink: 0 }}>
          {visibleNavItems.map(item => navItem(item.path, item.icon, item.label, item.badge))}
          {navHasMore && showMoreRow(navExpanded, () => setNavExpanded(v => !v))}
        </div>

        {/* Projets + Récents — un seul conteneur scrollable qui remplit la hauteur
            restante (le menu principal ci-dessus et le compte utilisateur ci-dessous
            restent fixes, jamais inclus dans ce scroll) */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* Projets */}
          <div style={{ padding: collapsed ? '4px 6px' : '4px 8px' }}>
            {projectsLocked ? (
              <div
                title={t('projects_locked_tooltip')}
                onClick={() => setUpgradeModalOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: collapsed ? '10px 0' : '9px 12px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  borderRadius: 8, cursor: 'pointer',
                  color: 'var(--black)', fontSize: 14, opacity: 0.4,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}><FolderPlus size={16} /></span>
                {!collapsed && <span style={{ flex: 1 }}>{t('nav_projects')}</span>}
                {!collapsed && <Lock size={12} style={{ flexShrink: 0 }} />}
              </div>
            ) : (
              navItem('/projets', <FolderPlus size={16} />, t('nav_projects'))
            )}
            {/* Liste des projets sous le lien principal */}
            {!collapsed && visibleProjects.length > 0 && (
              <div style={{ paddingLeft: 8, marginTop: 2 }}>
                {visibleProjects.map(p => {
                  // Trois états, comme la page Projets. « Actif » ne doit pas
                  // s'afficher tant que les documents s'extraient : le projet
                  // n'est pas encore exploitable pour une analyse.
                  // ⚠️ Le glyphe change en plus de la couleur — une pastille de
                  // 9 px distinguée par la seule teinte est illisible pour qui
                  // perçoit mal les couleurs.
                  const enExtraction = Boolean(p.extraction_in_progress);
                  const actif = p.status === 'active' && !enExtraction;
                  const fond = enExtraction ? 'rgba(59,130,246,0.12)' : actif ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)';
                  const teinte = enExtraction ? '#1e40af' : actif ? '#059669' : '#b45309';
                  return (
                  <Link key={p.id} href={`/projets?id=${p.id}`}>
                    <div
                      className="sidebar-item-hover"
                      title={enExtraction ? t('project_status_processing') : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: currentProjectId === p.id ? 'rgba(59,130,246,0.07)' : 'transparent',
                      }}
                    >
                      <FolderOpen size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 12, color: 'var(--black)', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name}
                      </span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
                        background: fond, color: teinte, flexShrink: 0,
                      }}>
                        {enExtraction ? '◐' : actif ? '●' : '○'}
                      </span>
                    </div>
                  </Link>
                  );
                })}
                {projectsHasMore && showMoreRow(projectsExpanded, () => setProjectsExpanded(v => !v))}
              </div>
            )}
          </div>

          {/* Récents */}
          {!collapsed && recentAnalyses.length > 0 && (
            <div style={{ padding: '0 8px' }}>
              <div style={{ padding: '6px 12px 4px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textTransform: 'uppercase' }}>{t('nav_recent')}</div>
              {visibleRecent.map((a, idx) => {
                const id = a.id || a.analysis_id;
                return (
                  <div key={id} style={{ position: 'relative' }}>
                    <div className="sidebar-item-hover" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s' }}>
                      <Bot size={12} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
                      <span
                        style={{ flex: 1, fontSize: 12, color: 'var(--black)', opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        onClick={() => setLocation(`/analysis/${id}`)}
                      >{a.filename}</span>
                      <button
                        className="sidebar-dot-btn"
                        onClick={(e) => { e.stopPropagation(); setDotMenuIdx(dotMenuIdx === idx ? null : idx); }}
                        style={{ width: 22, height: 22, borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      ><MoreHorizontal size={14} /></button>
                    </div>
                    {dotMenuIdx === idx && (
                      <div ref={dotMenuRef} style={{ position: 'absolute', right: 8, top: 32, zIndex: 100, background: 'var(--bg-surface)', border: '1px solid var(--gray)', borderRadius: 8, boxShadow: 'var(--shadow-md)', minWidth: 160, overflow: 'hidden' }}>
                        {[
                          { icon: <Copy size={13} />,   label: t('copy_summary'), action: () => handleCopyAnalysis(a) },
                          { icon: <Trash2 size={13} />, label: t('delete'),       action: () => handleDeleteAnalysis(id), danger: true },
                        ].map(item => (
                          <div key={item.label} onClick={item.action}
                            style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', color: item.danger ? '#ef4444' : 'var(--black)', display: 'flex', alignItems: 'center', gap: 8 }}
                            onMouseEnter={e => e.currentTarget.style.background = item.danger ? 'rgba(239,68,68,0.08)' : 'rgba(128,128,128,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            {item.icon}{item.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {recentHasMore && showMoreRow(recentExpanded, () => setRecentExpanded(v => !v))}
            </div>
          )}
        </div>

        {/* Logo de la licence (Cabinet ou Entreprise), vu par tous ses sièges.
            Chargé avec l'en-tête d'authentification : cf `useLogoLicence`. */}
        {!collapsed && logoLicence && (
          <div style={{ padding: '8px 16px 4px', flexShrink: 0 }}>
            <img
              src={logoLicence}
              alt={user.license.nom_affiche || user.license.nom || ''}
              style={{ maxHeight: 36, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
        )}

        {/* Compte utilisateur */}
        <div style={{ padding: collapsed ? '8px 6px' : '8px 8px', borderTop: '1px solid var(--gray)', flexShrink: 0, position: 'relative' }} ref={userMenuRef}>
          {userMenuOpen && (
            <div style={{ position: 'absolute', bottom: '100%', left: 8, right: 8, marginBottom: 6, zIndex: 100, background: 'var(--bg-surface)', border: '1px solid var(--gray)', borderRadius: 10, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
              {[
                { icon: <Settings size={14} />, label: t('menu_settings'), action: () => { setUserMenuOpen(false); setLocation('/parametres'); } },
                // Juste sous « Paramètres » : l'abonnement était un onglet qu'il
                // fallait deviner, alors que c'est là qu'on va pour souscrire ou
                // relever son solde d'analyses.
                // ⚠️ Masquée pour qui n'a pas d'abonnement à lui — un siège de
                // licence. L'entrée n'était filtrée NULLE PART : elle menait à
                // `?tab=billing`, un onglet que `Settings.js` masque pour ces
                // comptes, et la page se repliait sur « Profil ». Un cul-de-sac
                // sans message ni erreur. La règle est partagée (`lib/acces.js`)
                // pour qu'elle ne puisse plus diverger entre les deux écrans.
                ...(gereSonAbonnement(user) ? [
                  { icon: <CreditCard size={14} />, label: t('menu_billing'), action: () => { setUserMenuOpen(false); setLocation('/parametres?tab=billing'); } },
                ] : []),
                { icon: <Sliders size={14} />,  label: t('menu_prefs'),    action: () => { setUserMenuOpen(false); setLocation('/parametres?tab=preferences'); } },
                // Registre des mises à jour — placé à côté de l'aide, comme
                // partout : c'est là qu'on le cherche quand le pop-up a été fermé.
                { icon: <Sparkles size={14} />, label: t('nav_whatsnew'),  action: () => { setUserMenuOpen(false); setLocation('/nouveautes'); } },
                // « Obtenir de l'aide » pointait sur /privacy — copier-coller de la
                // ligne menu_terms juste en dessous, et il n'existait aucune page
                // d'aide. Corrigé le 31/08/2026 à l'arrivée de /aide. Nouvel onglet
                // comme menu_terms : on consulte l'aide quand quelque chose bloque,
                // et l'analyse en cours ne doit pas disparaître de l'écran.
                { icon: <HelpCircle size={14} />,label: t('menu_help'),    action: () => window.open('/aide', '_blank') },
                { icon: <FileText size={14} />,  label: t('menu_terms'),   action: () => window.open('/cgv', '_blank') },
              ].map(item => (
                <div key={item.label} onClick={item.action}
                  style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: 'var(--black)', display: 'flex', alignItems: 'center', gap: 10 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(128,128,128,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {item.icon}{item.label}
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--gray)', margin: '4px 0' }} />
              <div onClick={handleLogout}
                style={{ padding: '10px 14px', fontSize: 13, cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 10 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <LogOut size={14} />{t('menu_logout')}
              </div>
            </div>
          )}
          <div onClick={() => setUserMenuOpen(!userMenuOpen)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '8px 0' : '8px 10px', justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: 8, cursor: 'pointer', background: userMenuOpen ? 'rgba(128,128,128,0.08)' : 'transparent' }}
            onMouseEnter={e => { if (!userMenuOpen) e.currentTarget.style.background = 'rgba(128,128,128,0.06)'; }}
            onMouseLeave={e => { if (!userMenuOpen) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {(displayLabel).charAt(0).toUpperCase()}
            </div>
            {!collapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</div>
                {/* Appartenance : un siège doit savoir à quel cabinet ou à quelle
                    société il est rattaché (15/09/2026). `nom_affiche` et non
                    `nom` : un Cabinet pas encore nommé montre l'adresse de son
                    titulaire, jamais le « Cabinet — <email> » généré. */}
                {user?.license?.nom_affiche && (
                  <div title={user.license.nom_affiche}
                    style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.license.nom_affiche}
                  </div>
                )}
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {/* Deux lectures, et c'est voulu (décision du 15/09/2026) :
                      - licence et forfait → CONSOMMÉES / incluses. Le
                        dépassement n'y bloque pas, il se facture : le compteur
                        doit pouvoir passer au-delà (« 31/30 »). En « restantes »,
                        il restait figé à 0 dès le forfait dépassé.
                      - essai gratuit → RESTANTES / plafond : c'est un vrai
                        plafond, ce qui reste est ce qui compte. */}
                  {/* ⚠️ Un siège doté d'un plafond INDIVIDUEL voit ce plafond,
                      pas le pool de la licence : c'est lui qui le bloquera
                      (`_check_license_quotas`, étape 3). Il ne voyait que le pool,
                      et se découvrait bloqué sans l'avoir vu venir (17/09/2026). */}
                  {!['admin','dev'].includes(user?.role) && user?.license && user.license.quota_membre != null
                    ? <span title={t('sidebar_quota_personnel')}>{`${user.license.quota_membre_used ?? 0}/${user.license.quota_membre} ${t('sidebar_analyses_suffix')}`}</span>
                  : !['admin','dev'].includes(user?.role) && user?.license
                    ? <span title={t('sidebar_pool_licence')}>{`${user.license.pool_used ?? '—'}/${user.license.pool_analyses_count ?? '—'} ${t('sidebar_analyses_suffix')}`}</span>
                    : !['admin','dev'].includes(user?.role) && user?.tier_analyses_incluses != null
                      ? `${user.tier_analyses_consommees ?? 0}/${user.tier_analyses_incluses} ${t('sidebar_analyses_suffix')}`
                      : !['admin','dev'].includes(user?.role) && user?.quota_remaining != null
                        ? `${user.quota_remaining}/${user.quota_max} ${t('sidebar_analyses_suffix')}`
                        : roleLabelMap[user?.role] || 'Utilisateur'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {upgradeModalOpen && <UpgradeModal onClose={() => setUpgradeModalOpen(false)} />}
    </>
  );
};

export default Sidebar;
